import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BreakableRenderer, BusinessComponent, BusinessComponentOptions, CellPanel, ColumnComposition, ContentProvider, ImageRenderer, PageBreak, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { EsgConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, PaConfigKey, PcConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DisplayDataFactoryService, TabData, TabUtil } from '@ddt-web/data-service';
import { EsgScoreImpactUtil, extractAsOfDateFromMultiplePortfolios } from '@ddt-web/esg';
import { DEFAULT_DISCLOSURE_TEXT_CLASSES, NILE_DISCLOSURE_TEXT_CLASSES } from '@ddt-web/pdf-utils';
import { of } from 'rxjs';

import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingComponent, HeadingLevel } from '../heading';
import { EsgScoreColumnComposition } from '../helpers/column-compositions/esg-impact-table-composition';
import { TableCompositionHelper } from '../helpers/column-compositions/table-composition-helper';
import { SvgImage } from '../helpers/static-pages/static-section/svg-image';
import { ESGMetricsHeadings } from './esg-metrics-headings.model';

export class ESGScoreImpact extends BusinessComponent {
	@InjectConfig(PaConfigKey.EsgIndustryInvolvement) private readonly esgIndustryInvolvementMetrics: any[] = EsgConstants.INDUSTRY_INVOLVEMENT_METRICS;
	@InjectConfig(SharedConfigKey.EsgCarbonEquivalencies) private readonly esgMetricEquivalencyMap: { [equivalency: string]: number } = {};
	@InjectConfig(PcConfigKey.SHOW_AS_OF_DATE_DYNAMIC) public readonly showAsOfDateDynamic: boolean = EsgConstants.SHOW_AS_OF_DATE_DYNAMIC_DEFAULT;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_ROUND_OFF_EQUIVALENCY_SCORE) public readonly enableRoundOff: boolean = false;

	private esgScoreImpactTab: TabData;
	private filteredComponentData: ComponentData;

	public constructor(
		public name: string,
		public dataComponentId: string,
		public contentService: ContentService,
		public headlines: ESGMetricsHeadings,
		public isIndustryInvolvement: boolean = false,
		public shouldStartFromNewPage: boolean = false,
		public disclosureTextClasses: string[] = DEFAULT_DISCLOSURE_TEXT_CLASSES,
		public visible: boolean = true,
		public filteredPortfoliosWithoutIndexes?: string[],
		public displayDataFactoryService?: DisplayDataFactoryService
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		if (this.filteredPortfoliosWithoutIndexes?.length) {
			this.displayDataFactoryService.getDisplayableComponentData(dataComponentId, of(this.filteredPortfoliosWithoutIndexes), new ContentService())
				.subscribe(res => {
					this.filteredComponentData = res;
				});
		}
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		this.esgScoreImpactTab = this.isIndustryInvolvement ? componentData?.getTabById('esg.industryInvolvement') : componentData?.getTabById('esg.carbonIntensity');
		return this.visible && componentData?.success && TabUtil.isTabValid(this.esgScoreImpactTab);
	}

	public buildComponent(componentData: ComponentData, contentProvider: ContentProvider, applicationState?: any): Array<AbstractRenderer<SVGGElement>> {
		componentData = this.filteredPortfoliosWithoutIndexes?.length && this.filteredComponentData ? this.filteredComponentData : componentData;

		const { headingDcr, heading, headlineDcr, headline, showDisclaimer, isClientReport } = this.headlines;

		const customHeadlineDcr = isClientReport ? headlineDcr + '_CLIENT_REPORT' : headlineDcr;

		const panels: AbstractRenderer<SVGGElement>[] = [];
		if (this.isComponentDataVisible(componentData)) {
			const asOfDate = extractAsOfDateFromMultiplePortfolios(componentData.getTabById('esg.asOfDate'));
			panels.push(
				...this.shouldStartFromNewPage ? [new PageBreak()] : [],
				new HeadingComponent(headingDcr, heading, HeadingLevel.H2),
				new StaticParagraphComponent(customHeadlineDcr, headline, {}, { marginBottom: 10 }, ['text-10']),
				...this.getEsgScoreImpactSection(contentProvider)
			);

			if (showDisclaimer) {
				let disclaimerKey = this.isIndustryInvolvement ? 'ESG_INDUSTRY_INVOLVEMENT_DISCLOSURE_PDF' : 'ESG_SCORE_IMPACT_DISCLOSURE_PDF';
				disclaimerKey = isClientReport ? disclaimerKey + '_CLIENT_REPORT' : disclaimerKey;
				panels.push(new DisclosureComponent(disclaimerKey, disclaimerKey, {}, {}, this.disclosureTextClasses));
				if (this.showAsOfDateDynamic) { panels.push(new DisclosureComponent('AS_OF_DATE_DISCLOSURE', 'As of: {0}', { 0: asOfDate }, {}, NILE_DISCLOSURE_TEXT_CLASSES)); }
			}
		}
		return panels;
	}

	private getEsgScoreImpactSection(contentProvider: ContentProvider): Array<AbstractRenderer<SVGGElement>> {
		const rows: Array<RowPanel> = [];
		const headers = this.esgScoreImpactTab.tableData[0]?.headersX;
		const multiPortfolios = headers?.length > 1;


		if (multiPortfolios) {
			const headerColumnProportions = this.getTableColumnProportions(headers?.length);
			const tableDefinitions = new EsgScoreColumnComposition().getColumnCompositions(
				this.esgScoreImpactTab,
				headerColumnProportions,
				this.esgMetricEquivalencyMap,
				this.esgIndustryInvolvementMetrics,
				this.isIndustryInvolvement,
				contentProvider,
				this.enableRoundOff
			);

			const headerRow: RowPanel = TableCompositionHelper.getHeaderRowPanel(
				'ESG Score Impact Header Row',
				tableDefinitions.headers,
				{ marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#000' });

			rows.push(headerRow);

			tableDefinitions?.body?.forEach((configs: Array<ColumnComposition>, index: number) => {
				const isNotLastRow: boolean = index < (tableDefinitions.body.length - 1);
				const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
				const boxModelConstraint = { marginBottom: isNotLastRow ? 5 : 10, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#616161' };
				rows.push(new RowPanel('bodyRow', rowRenderer, boxModelConstraint, Alignment.Horizontal.Left, Alignment.Vertical.Middle));
			});


			return [new BreakableRenderer(rows, {}, headerRow)];

		} else { // single portfolio
			return this.getSinglePortfolioRows();
		}
	}


	private getSinglePortfolioRows(): AbstractRenderer<SVGGElement>[] {
		const rows: AbstractRenderer<SVGGElement>[] = [];

		rows.push(
			new RowPanel(
				'CARBON_IMPACT_ROW',
				[...this.getSinglePortfolioCells()],
				{ useFullWidth: true, marginBottom: 10 },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			)
		);
		return rows;
	}

	private getSinglePortfolioCells(): AbstractRenderer<SVGGElement>[] {
		const cells: AbstractRenderer<SVGGElement>[] = [];
		const cellsData = this.esgScoreImpactTab.tableData[0]?.cells;
		const esgMetrics = this.isIndustryInvolvement ? this.esgIndustryInvolvementMetrics : Object.keys(this.esgMetricEquivalencyMap);
		esgMetrics.forEach((metric) => {
			let equivalencyScore;
			if (this.isIndustryInvolvement) {
				const industryInvolvementCell = cellsData.find(cell => cell[0]?.config?.id === metric);
				equivalencyScore = industryInvolvementCell?.[0]?.formattedValue;
			} else {
				if (metric === 'CARBON_EMISSION_DIFFERENCE') {
					equivalencyScore = EsgScoreImpactUtil.getCarbonEmissionDifference(cellsData[0][0], cellsData[1][0]);
				}
				else {
					equivalencyScore = EsgScoreImpactUtil.getCarbonImpactScore(cellsData[0][0], metric, this.esgMetricEquivalencyMap, this.enableRoundOff);
				}
			}
			cells.push(
				new CellPanel(
					[...this.getCellRows(metric, equivalencyScore)],
					{
						proportion: 100 / esgMetrics.length, borderLeft: 0.5, borderBottom: 0.5, borderRight: 0.5,
						borderTop: 0.5, borderTopColor: '#616161', borderBottomColor: '#616161', borderLeftColor: '#616161', borderRightColor: '#616161', marginRight: 10,
						useFullHeight: true
					},
					Alignment.Horizontal.Left
				)
			);
		});
		return cells;
	}

	private getCellRows(metric: string, equivalencyScore: string): AbstractRenderer<SVGGElement>[] {
		const rows: AbstractRenderer<SVGGElement>[] = [];
		rows.push(
			new RowPanel(
				'CARBON_METRIC_IMAGE_ROW',
				[new CellPanel([
					new ImageRenderer(new SvgImage(metric).getStaticContent())
				], { proportion: 100, marginTop: 10, marginLeft: 10, marginRight: 10, height: 40, width: 40 }, Alignment.Horizontal.Left, null, Alignment.Vertical.Bottom)
				],
				{ useFullWidth: true },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			)
		);

		rows.push(
			new RowPanel(
				'CARBON_METRIC_IMPACT_ROW',
				[new CellPanel([
					new StaticParagraphComponent('', equivalencyScore, {}, {}, ['text-12', 'text-bold'])
				], { proportion: 100, marginTop: 10, marginLeft: 10, marginRight: 10 }, Alignment.Horizontal.Left, null, Alignment.Vertical.Bottom)
				],
				{ useFullWidth: true },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			)
		);

		rows.push(
			new RowPanel(
				'CARBON_METRIC_NAME_ROW',
				[new CellPanel([
					new StaticParagraphComponent('ESG_METRIC_' + metric + '_LABEL', metric, {}, { paddingTop: 0.1, paddingLeft: 10, paddingRight: 10, paddingBottom: 10 })
				], { proportion: 100 }, Alignment.Horizontal.Left)
				],
				{ useFullWidth: true },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Top
			)
		);
		return rows;

	}

	private getTableColumnProportions(portCount: number): Array<number> {
		switch (portCount) {
			case 3:
				return [40, 20, 20, 20];
			case 4:
				return [40, 15, 15, 15, 15];
			case 5:
				return [25, 15, 15, 15, 15, 15];
			case 6:
				return [22, 13, 13, 13, 13, 13, 13];
			case 7:
				return [23, 11, 11, 11, 11, 11, 11, 11];
			case 8:
				return [20, 10, 10, 10, 10, 10, 10, 10, 10];
			case 9:
				return [19, 9, 9, 9, 9, 9, 9, 9, 9, 9];
			case 10:
				return [20, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8];
			case 11:
				return [12, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8];
			default:
				return [40, 30, 30];
		}
	}
}
